import { Finance16 } from '@carbon/icons-react';
import ChevronDown16 from '@carbon/icons-react/es/chevron--down/16';
import Close16 from '@carbon/icons-react/es/close/16';
import ListChecked16 from '@carbon/icons-react/es/list--checked/16';
import Task16 from '@carbon/icons-react/es/task/16';
import Timer16 from '@carbon/icons-react/es/timer/16';
import UserData16 from '@carbon/icons-react/es/user--data/16';
import { Form as AntForm } from 'antd';
import { flatten } from 'lodash';
import React, { FC, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { ReactComponent as PhaseIcon } from 'assets/svgs/phase-icon-without-fill.svg';
import { Button } from 'components/atoms/button';
import {
  GroupedDropdown,
  GroupIndicatorIcon,
  StyledTrigger,
  TextWithEllipsis,
} from 'components/molecules/grouped-dropdown';
import { getFilterGroupType } from 'components/pages/filter-transformer';
import { useTranslation } from 'hooks/use-translation';
import { FilterableField, GroupedOptions } from '../fields';
import {
  GetFilterOperationTypeOptions,
  GetOperationOptionRenderer,
  GetSelectEditorComponents,
} from '../filter-box/utils';
import { GroupCriteria } from '../grouped-filter-popover';
import { Text } from './components';
import { criterion } from './criterion';
import { getDefaultCriteria } from './get-default-criteria';

const StyledText = styled(Text)`
  min-width: 40px;
`;

const Form = styled(AntForm)`
  .ant-row.ant-form-item:last-child {
    margin-right: 0;
  }
`;

export const iconForGroupedFilter = {
  task: <ListChecked16 />,
  project: <Task16 />,
  phase: <PhaseIcon fill="currentColor" />,
  user: <UserData16 />,
  timesheet: <Timer16 />,
  financials: <Timer16 />,
  allocations: <Timer16 />,
  utilisations: <Timer16 />,
  role: <Timer16 />,
  financial: <Finance16 fill="currentColor" />,
};

const TextWithIconWrapper = styled.div`
  display: inline-flex;
  align-items: center;
  max-width: 190px;
`;

const TriggerComponent = ({ label, sourceType }) => {
  const defaultFallback = useTranslation('select_with_ellipsis');

  return (
    <StyledTrigger data-cy="select.fields">
      <TextWithIconWrapper>
        {sourceType ? (
          <GroupIndicatorIcon>
            {iconForGroupedFilter[sourceType]}
          </GroupIndicatorIcon>
        ) : null}
        <TextWithEllipsis>{label || defaultFallback}</TextWithEllipsis>
      </TextWithIconWrapper>
      <ChevronDown16 />
    </StyledTrigger>
  );
};

interface Props {
  hideMatchTypeText?: boolean;
  text: string;
  fields: GroupedOptions<FilterableField>[];
  filter?: GroupCriteria;
  onChange: (filter: GroupCriteria) => void;
  onRemove?: () => void;
  getFilterOperationTypeOptions?: GetFilterOperationTypeOptions;
  getSelectEditorComponents?: GetSelectEditorComponents;
  getOperationOptionRenderer?: GetOperationOptionRenderer;
}

export const GroupedCriteria: FC<Props> = ({
  hideMatchTypeText = false,
  fields,
  text,
  filter,
  onChange,
  onRemove,
  getFilterOperationTypeOptions,
  getSelectEditorComponents,
  getOperationOptionRenderer,
}) => {
  const allFields = useMemo(
    () => flatten(fields.map((x) => x.options)),
    [fields],
  );

  const _onChange = useCallback(
    (selected) => {
      const groupCriteria = allFields.find(
        (f) => f.fieldId === selected.fieldId,
      )!;

      onChange({
        id: getFilterGroupType(selected.sourceType),
        criteria: getDefaultCriteria(groupCriteria),
      });
    },
    [allFields, onChange],
  );

  return (
    <Form layout="inline">
      <Form.Item>
        <Button
          type="text"
          shape="circle"
          size="small"
          icon={<Close16 />}
          style={{ visibility: onRemove != null ? 'visible' : 'hidden' }}
          onClick={onRemove}
        />
      </Form.Item>
      {hideMatchTypeText ? null : (
        <Form.Item>
          <StyledText>{text}</StyledText>
        </Form.Item>
      )}
      <Form.Item>
        <GroupedDropdown
          triggerComponent={
            <TriggerComponent
              label={filter?.criteria?.field?.fieldLabel}
              sourceType={filter?.criteria?.field?.sourceType}
            />
          }
          title={text}
          value={{
            ...filter?.criteria?.field,
            label: filter?.criteria?.field?.fieldLabel,
            value: filter?.criteria?.field?.fieldId,
          }}
          selectProps={{
            onChange: _onChange,
            options: fields,
          }}
        />
      </Form.Item>
      {criterion({
        filter: filter?.criteria,
        onChange: (fieldFilter) =>
          onChange({
            id: getFilterGroupType(fieldFilter.field.sourceType),
            criteria: fieldFilter,
          }),
        getFilterOperationTypeOptions,
        getSelectEditorComponents,
        getOperationOptionRenderer,
      })}
    </Form>
  );
};
